import { Moon, Sun } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn.ts';

function getInitialTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem('theme');
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<'light' | 'dark'>(getInitialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((th) => (th === 'light' ? 'dark' : 'light'));
  }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={theme === 'light' ? t('a11y.switchToDarkMode') : t('a11y.switchToLightMode')}
      className={cn(
        'inline-flex items-center justify-center size-9 rounded-lg border border-border bg-bg-card text-fg-2 hover:text-fg hover:border-accent transition-all duration-260 ease-[var(--ease-out-expo)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring cursor-pointer',
        className,
      )}
    >
      {theme === 'light' ? <Moon className="size-4" /> : <Sun className="size-4" />}
    </button>
  );
}
